import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Plus, Pencil, Trash2, Wine } from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  { value: "starters", label: "Starters" },
  { value: "mains", label: "Mains" },
  { value: "sides", label: "Sides & breads" },
  { value: "desserts", label: "Desserts" },
  { value: "drinks_non_alc", label: "Soft drinks & mocktails" },
  { value: "drinks_alc", label: "Wine, beer & cocktails" },
];

const ALLERGENS = ["nuts", "peanut", "dairy", "gluten", "egg", "shellfish", "soy", "sesame", "fish", "mustard"];

const blank = { name: "", category: "starters", list_price_inr: 400, price_inr: 350, veg_flag: true, tags: "", allergens: [], spice_level: 0, must_try: false, description: "" };

export default function PartnerMenu() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [filter, setFilter] = useState("all");
  const [csvOpen, setCsvOpen] = useState(false);
  const [csvBusy, setCsvBusy] = useState(false);
  const [csvSummary, setCsvSummary] = useState(null);

  const load = async () => {
    const { data } = await api.get("/partner/menu-items");
    setList(data);
  };
  useEffect(() => { load(); }, []);

  const grouped = useMemo(() => {
    const filtered = filter === "all" ? list : list.filter((m) => m.category === filter);
    return filtered.reduce((acc, m) => { (acc[m.category] = acc[m.category] || []).push(m); return acc; }, {});
  }, [list, filter]);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (m) => {
    setEditing(m);
    setForm({
      name: m.name,
      category: m.category,
      list_price_inr: m.list_price_inr || m.price_inr,
      price_inr: m.price_inr,
      veg_flag: m.veg_flag,
      tags: (m.tags || []).join(", "),
      allergens: m.allergens || [],
      spice_level: m.spice_level || 0,
      must_try: !!m.must_try,
      description: m.description || "",
    });
    setOpen(true);
  };

  const toggleAllergen = (a) => {
    setForm((f) => ({
      ...f,
      allergens: f.allergens.includes(a) ? f.allergens.filter((x) => x !== a) : [...f.allergens, a],
    }));
  };

  const handleCsv = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCsvBusy(true);
    setCsvSummary(null);
    try {
      const text = await file.text();
      const { data } = await api.post("/partner/menu-items/bulk-csv", { csv: text });
      setCsvSummary(data);
      toast.success(`Created ${data.created} · Updated ${data.updated} · Skipped ${data.skipped}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "CSV upload failed");
    } finally {
      setCsvBusy(false);
      e.target.value = "";
    }
  };

  const save = async () => {
    const tabloPrice = parseInt(form.price_inr) || 0;
    const listPrice = parseInt(form.list_price_inr) || tabloPrice;
    if (listPrice < tabloPrice) return toast.error("MRP can't be less than Tablo price");
    const body = {
      name: form.name.trim(),
      category: form.category,
      price_inr: tabloPrice,
      list_price_inr: listPrice,
      veg_flag: !!form.veg_flag,
      tags: String(form.tags).split(",").map((s) => s.trim()).filter(Boolean),
      allergens: form.allergens || [],
      spice_level: parseInt(form.spice_level) || 0,
      must_try: !!form.must_try,
      description: (form.description || "").slice(0, 280),
    };
    if (!body.name || body.price_inr <= 0) return toast.error("Name + positive price required");
    try {
      if (editing) {
        await api.patch(`/partner/menu-items/${editing.id}`, body);
        toast.success("Item updated");
      } else {
        await api.post("/partner/menu-items", body);
        toast.success("Item added");
      }
      setOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/partner/menu-items/${id}`); toast.success("Deleted"); load(); } catch (e) { toast.error("Failed"); }
  };

  const toggleActive = async (m) => {
    try { await api.patch(`/partner/menu-items/${m.id}`, { active_flag: !m.active_flag }); load(); } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="space-y-5 animate-fade-up">
      <header className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-amber text-xs uppercase tracking-[0.18em] font-medium mb-2">Menu</p>
          <h1 className="font-serif text-4xl">Your menu</h1>
          <p className="text-light-ink text-sm mt-2">Items are shown when bookers pick their own dishes or when Tablo Curate AI builds a set menu. PDF/image upload coming soon.</p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <label className={`tablo-btn-outline cursor-pointer text-sm ${csvBusy ? "opacity-50 pointer-events-none" : ""}`} data-testid="menu-csv-upload-label">
            <input type="file" accept=".csv,text/csv" onChange={handleCsv} className="hidden" data-testid="menu-csv-input"/>
            {csvBusy ? "Uploading…" : "Upload CSV"}
          </label>
          <a
            href={`data:text/csv;charset=utf-8,name,category,price_inr,list_price_inr,veg_flag,spice_level,must_try,allergens,tags,description%0APaneer Tikka,starters,420,520,yes,2,yes,dairy|nuts,signature,Tandoor classic`}
            download="tablo-menu-sample.csv"
            className="text-xs text-amber underline"
            data-testid="menu-csv-sample"
          >Sample CSV</a>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-44" data-testid="menu-filter"><SelectValue/></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="tablo-btn-primary" data-testid="menu-new-btn"><Plus className="w-4 h-4 mr-1" strokeWidth={2}/>New item</Button>
            </DialogTrigger>
            <DialogContent className="max-w-md bg-cream">
              <DialogHeader><DialogTitle className="font-serif text-2xl">{editing ? "Edit" : "Add"} menu item</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div>
                  <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Name</label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="bg-white border-tablo-border" data-testid="menu-name"/>
                </div>
                <div className="grid grid-cols-3 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Category</label>
                    <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                      <SelectTrigger className="bg-white border-tablo-border" data-testid="menu-category"><SelectValue/></SelectTrigger>
                      <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">MRP (₹)</label>
                    <Input type="number" value={form.list_price_inr} onChange={(e) => setForm({ ...form, list_price_inr: e.target.value })} className="bg-white border-tablo-border" data-testid="menu-list-price"/>
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Tablo (₹)</label>
                    <Input type="number" value={form.price_inr} onChange={(e) => setForm({ ...form, price_inr: e.target.value })} className="bg-white border-tablo-border" data-testid="menu-price"/>
                  </div>
                </div>
                {(() => {
                  const lp = parseInt(form.list_price_inr) || 0;
                  const tp = parseInt(form.price_inr) || 0;
                  const pct = lp > tp && lp > 0 ? Math.round(((lp - tp) / lp) * 100) : 0;
                  return (
                    <div className="text-xs flex items-center justify-between bg-cream rounded-lg px-3 py-2" data-testid="menu-discount-preview">
                      <span className="text-light-ink">Guest sees discount:</span>
                      {pct > 0 ? (
                        <span className="font-medium text-amber">{pct}% OFF · save ₹{lp - tp}</span>
                      ) : (
                        <span className="text-light-ink/70">No discount — bump MRP above Tablo price.</span>
                      )}
                    </div>
                  );
                })()}
                <div>
                  <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Tags (comma-separated)</label>
                  <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="signature, jain, spicy" className="bg-white border-tablo-border"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Description (shown to bookers, max 280 chars)</label>
                  <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value.slice(0, 280) })} placeholder="Smoky tandoor, melt-in-mouth..." className="bg-white border-tablo-border" data-testid="menu-description"/>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Allergens present</label>
                  <div className="flex flex-wrap gap-1.5">
                    {ALLERGENS.map((a) => (
                      <button
                        key={a}
                        type="button"
                        onClick={() => toggleAllergen(a)}
                        className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.allergens.includes(a) ? "bg-rose-600 text-white border-rose-600" : "bg-white text-ink border-tablo-border hover:border-rose-400"}`}
                        data-testid={`menu-allergen-${a}`}
                      >{a}</button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-xs uppercase tracking-wider text-light-ink mb-1 block">Spice level</label>
                    <div className="flex gap-1">
                      {[0, 1, 2, 3].map((s) => (
                        <button
                          key={s}
                          type="button"
                          onClick={() => setForm({ ...form, spice_level: s })}
                          className={`text-xs flex-1 py-1.5 rounded-full border transition-colors ${form.spice_level === s ? "bg-rose-600 text-white border-rose-600" : "bg-white text-ink border-tablo-border"}`}
                          data-testid={`menu-spice-${s}`}
                        >{["None", "Mild", "Med", "Hot"][s]}</button>
                      ))}
                    </div>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 bg-cream rounded-xl px-3 py-2.5 w-full text-sm cursor-pointer">
                      <Switch checked={form.must_try} onCheckedChange={(v) => setForm({ ...form, must_try: v })} data-testid="menu-must-try"/>
                      <span>★ Chef's pick (must-try)</span>
                    </label>
                  </div>
                </div>
                <div className="flex items-center justify-between bg-cream rounded-xl px-3 py-2">
                  <span className="text-sm">Vegetarian</span>
                  <Switch checked={form.veg_flag} onCheckedChange={(v) => setForm({ ...form, veg_flag: v })} data-testid="menu-veg"/>
                </div>
                <Button onClick={save} className="w-full tablo-btn-primary" data-testid="menu-save-btn">{editing ? "Save changes" : "Add item"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </header>

      {list.length === 0 && <div className="tablo-card p-12 text-center text-light-ink">No menu items yet — tap "New item" above to start.</div>}

      {CATEGORIES.filter((c) => grouped[c.value]?.length).map((c) => (
        <div key={c.value}>
          <div className="flex items-center gap-2 mb-2">
            {c.value === "drinks_alc" && <Wine className="w-4 h-4 text-amber" strokeWidth={1.6}/>}
            <p className="text-xs uppercase tracking-[0.18em] text-amber font-medium">{c.label}</p>
            <span className="text-xs text-light-ink">· {grouped[c.value].length} items</span>
          </div>
          <div className="tablo-card divide-y divide-tablo-border/60">
            {grouped[c.value].map((m) => (
              <div key={m.id} className="flex items-center justify-between p-3 gap-3" data-testid={`menu-row-${m.id}`}>
                <div className="flex items-start gap-2 flex-1 min-w-0">
                  <span className={`mt-1.5 inline-block w-2 h-2 rounded-full shrink-0 ${m.veg_flag ? "bg-green-600" : "bg-rose-600"}`}/>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 flex-wrap">
                      <p className="font-medium text-sm">{m.name}</p>
                      {m.must_try && (
                        <span className="text-[10px] uppercase tracking-wider bg-amber/30 text-ink px-1.5 py-0.5 rounded-full font-medium">★ Pick</span>
                      )}
                      {m.spice_level > 0 && (
                        <span className="text-[10px]" title={`Spice ${m.spice_level}/3`}>{"🌶️".repeat(m.spice_level)}</span>
                      )}
                    </div>
                    {m.allergens?.length > 0 && (
                      <p className="text-[10px] text-rose-700/90 mt-0.5">Contains: {m.allergens.join(", ")}</p>
                    )}
                    {m.tags?.length > 0 && <p className="text-[11px] text-light-ink uppercase tracking-wider">{m.tags.join(" · ")}</p>}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  {m.list_price_inr > m.price_inr && <p className="text-[11px] text-light-ink/70 line-through leading-3">₹{m.list_price_inr}</p>}
                  <p className="text-sm font-medium">₹{m.price_inr}</p>
                </div>
                <Switch checked={!!m.active_flag} onCheckedChange={() => toggleActive(m)} data-testid={`menu-active-${m.id}`}/>
                <button onClick={() => openEdit(m)} className="p-1.5 hover:bg-cream rounded" data-testid={`menu-edit-${m.id}`}><Pencil className="w-4 h-4" strokeWidth={1.6}/></button>
                <button onClick={() => remove(m.id)} className="p-1.5 hover:bg-rose-50 text-rose-600 rounded" data-testid={`menu-del-${m.id}`}><Trash2 className="w-4 h-4" strokeWidth={1.6}/></button>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
